import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, formatErr } from "@/lib/api";
import { EASE } from "@/lib/site";

const OPTIONS = {
  work_type: [["interieur", "Peinture intérieure"], ["exterieur", "Extérieur / façade"], ["revetements", "Revêtements murs & sols"], ["autre", "Autre"]],
  property_type: [["appartement", "Appartement"], ["maison", "Maison"], ["copropriete", "Copropriété"], ["local", "Local pro"]],
  timeline: [["urgent", "Urgent (< 1 mois)"], ["1-3mois", "Sous 1 à 3 mois"], ["3mois+", "Plus de 3 mois"], ["flexible", "Flexible"]],
};

const Chips = ({ name, label, value, onChange, dark }) => (
  <fieldset>
    <legend className={`eyebrow mb-3 ${dark ? "text-paper/75" : "text-slate-600"}`}>{label}</legend>
    <div className="flex flex-wrap gap-2">
      {OPTIONS[name].map(([v, l]) => {
        const on = value === v;
        return (
          <button type="button" key={v} onClick={() => onChange(name, v)} data-testid={`quote-${name}-${v}`} aria-pressed={on}
            className={`border px-4 py-2.5 text-sm transition-colors duration-300 ${on ? "border-gold bg-gold text-ink" : dark ? "border-paper/25 hover:border-paper/70" : "border-ink/20 hover:border-ink"}`}>
            {l}
          </button>
        );
      })}
    </div>
  </fieldset>
);

const Field = ({ label, dark, ...p }) => (
  <label className="block">
    <span className={`eyebrow ${dark ? "text-paper/75" : "text-slate-600"}`}>{label}</span>
    <input {...p} className={`mt-2 w-full border-b bg-transparent py-3 text-lg outline-none transition-colors duration-300 placeholder:text-slate-500 ${dark ? "border-paper/25 focus:border-gold" : "border-ink/20 focus:border-ink"}`} />
  </label>
);

const EMPTY = { name: "", phone: "", email: "", city: "", surface: "", message: "", work_type: "", property_type: "", timeline: "" };

export const QuoteForm = ({ dark = false, defaultType = "" }) => {
  const [f, setF] = useState({ ...EMPTY, work_type: defaultType });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onInput = (e) => set(e.target.name, e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.work_type || !f.property_type || !f.timeline) {
      toast.error("Merci de préciser le type de travaux, le bien et le délai.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...f, surface: f.surface ? Number(f.surface) : null });
      setDone(true);
      setF({ ...EMPTY, work_type: defaultType });
      toast.success("Demande envoyée ! Nous vous rappelons sous 24h.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative">
      <AnimatePresence>
        {done && (
          <motion.div
            data-testid="quote-success"
            initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className={`absolute inset-0 z-10 flex flex-col items-start justify-center p-8 ${dark ? "bg-ink" : "bg-paper"}`}
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-ink"><Check className="h-8 w-8" /></span>
            <h3 className="mt-8 font-display text-4xl sm:text-5xl">Merci, c'est bien reçu.</h3>
            <p className={`mt-4 max-w-md ${dark ? "text-paper/80" : "text-slate-700"}`}>Nous étudions votre projet et vous recontactons sous 24h ouvrées pour organiser la visite gratuite.</p>
            <button onClick={() => setDone(false)} data-testid="quote-new-request" className="mt-8 link-line text-sm font-semibold">Faire une autre demande</button>
          </motion.div>
        )}
      </AnimatePresence>
      <form onSubmit={submit} data-testid="quote-form" className="space-y-9">
        <Chips name="work_type" label="1. Type de travaux" value={f.work_type} onChange={set} dark={dark} />
        <Chips name="property_type" label="2. Votre bien" value={f.property_type} onChange={set} dark={dark} />
        <div className="grid gap-9 sm:grid-cols-2">
          <Field dark={dark} label="3. Surface approx. (m²)" name="surface" type="number" min="1" value={f.surface} onChange={onInput} placeholder="ex. 45" data-testid="quote-surface-input" />
          <Field dark={dark} label="4. Ville / code postal" name="city" required value={f.city} onChange={onInput} placeholder="ex. Avrillé 49240" data-testid="quote-city-input" />
        </div>
        <Chips name="timeline" label="5. Délai souhaité" value={f.timeline} onChange={set} dark={dark} />
        <div className="grid gap-9 sm:grid-cols-3">
          <Field dark={dark} label="Nom" name="name" required value={f.name} onChange={onInput} placeholder="Jean Dupont" data-testid="quote-name-input" />
          <Field dark={dark} label="Téléphone" name="phone" type="tel" required value={f.phone} onChange={onInput} placeholder="06 12 34 56 78" data-testid="quote-phone-input" />
          <Field dark={dark} label="E-mail" name="email" type="email" required value={f.email} onChange={onInput} placeholder="vous@email.fr" data-testid="quote-email-input" />
        </div>
        <label className="block">
          <span className={`eyebrow ${dark ? "text-paper/75" : "text-slate-600"}`}>Votre projet en quelques mots</span>
          <textarea name="message" rows={3} value={f.message} onChange={onInput} data-testid="quote-message-input" placeholder="Pièces concernées, état des supports, couleurs envisagées…"
            className={`mt-2 w-full resize-none border-b bg-transparent py-3 text-lg outline-none transition-colors duration-300 placeholder:text-slate-500 ${dark ? "border-paper/25 focus:border-gold" : "border-ink/20 focus:border-ink"}`} />
        </label>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className={`text-xs max-w-sm ${dark ? "text-paper/70" : "text-slate-600"}`}>Vos données servent uniquement à répondre à votre demande. Devis 100 % gratuit et sans engagement.</p>
          <button type="submit" disabled={sending} data-testid="quote-submit-button"
            className="group inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 font-semibold text-ink transition-colors duration-300 hover:bg-clay hover:text-paper disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Recevoir mon devis gratuit
            <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
          </button>
        </div>
      </form>
    </div>
  );
};
